import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const PetRemindersPage = () => {
  const navigate = useNavigate();
  const { petId } = useParams();

  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [pet, setPet] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [totalReminders, setTotalReminders] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Edit modal state
  const [editingReminder, setEditingReminder] = useState(null);
  const [editForm, setEditForm] = useState({
    reminder_date: '',
    message: '',
    status: 'pending',
    recurrence: 'none',
  });

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    reminder_date: '',
    message: '',
    recurrence: 'none',
  });

  // Template helpers
  const getPetFriendlyName = () => {
    return pet?.name || 'το κατοικίδιό σας';
  };
  const applyTemplateToEdit = (template) => {
    const petName = getPetFriendlyName();
    let message = '';
    if (template === 'monthly_pills') {
      message = `Ήρθε η ώρα για τα μηνιαία χάπια του ${petName}.`;
    } else if (template === 'vaccination') {
      message = `Ήρθε η ώρα για τον εμβολιασμό του ${petName}. Παρακαλώ κλείστε ραντεβού στο παρακάτω σύνδεσμο: ...`;
    } else if (template === 'recheck') {
      message = `Ήρθε η ώρα για τον επανέλεγχο του ${petName}. Παρακαλώ κλείστε ραντεβού για επανεξέταση.`;
    }
    setEditForm((prev) => ({ ...prev, message }));
  };
  const applyTemplateToCreate = (template) => {
    const petName = getPetFriendlyName();
    let message = '';
    if (template === 'monthly_pills') {
      message = `Ήρθε η ώρα για τα μηνιαία χάπια του ${petName}.`;
    } else if (template === 'vaccination') {
      message = `Ήρθε η ώρα για τον εμβολιασμό του ${petName}. Παρακαλώ κλείστε ραντεβού στο παρακάτω σύνδεσμο: ...`;
    } else if (template === 'recheck') {
      message = `Ήρθε η ώρα για τον επανέλεγχο του ${petName}. Παρακαλώ κλείστε ραντεβού για επανεξέταση.`;
    }
    setCreateForm((prev) => ({ ...prev, message }));
  };

  const openEdit = (rem) => {
    setEditingReminder(rem);
    setEditForm({
      reminder_date: rem.reminder_date ? new Date(rem.reminder_date).toISOString().slice(0,10) : '',
      message: rem.message || '',
      status: rem.status || 'pending',
      recurrence: rem.recurrence || 'none',
    });
  };

  const closeEdit = () => {
    setEditingReminder(null);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveReminderEdits = async () => {
    if (!editingReminder) return;
    try {
      const { error } = await supabase
        .from('reminders')
        .update({
          reminder_date: editForm.reminder_date ? new Date(editForm.reminder_date).toISOString() : editingReminder.reminder_date, // date-only
          message: editForm.message.trim(),
          status: editForm.status,
          recurrence: editForm.recurrence,
        })
        .eq('id', editingReminder.id)
        .eq('clinic_id', clinic.id);
      if (error) throw error;
      showToast('Η υπενθύμιση ενημερώθηκε.', 'success');
      closeEdit();
      await fetchReminders();
    } catch (err) {
      console.error('Failed to update reminder:', err);
      showToast('Σφάλμα κατά την ενημέρωση.', 'error');
    }
  };

  const [cancelConfirm, setCancelConfirm] = useState(null);

  const cancelReminder = async () => {
    if (!cancelConfirm) return;
    try {
      const { error } = await supabase
        .from('reminders')
        .update({ status: 'cancelled' })
        .eq('id', cancelConfirm.id)
        .eq('clinic_id', clinic.id);
      if (error) throw error;
      showToast('Η υπενθύμιση ακυρώθηκε.', 'success');
      setCancelConfirm(null);
      await fetchReminders();
    } catch (err) {
      console.error('Failed to cancel reminder:', err);
      showToast('Σφάλμα κατά την ακύρωση.', 'error');
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const deleteReminder = async () => {
    if (!deleteConfirm) return;
    try {
      const { error } = await supabase
        .from('reminders')
        .delete()
        .eq('id', deleteConfirm.id)
        .eq('clinic_id', clinic.id);
      if (error) throw error;
      showToast('Η υπενθύμιση διαγράφηκε.', 'success');
      setDeleteConfirm(null);
      await fetchReminders();
    } catch (err) {
      console.error('Failed to delete reminder:', err);
      showToast('Σφάλμα κατά τη διαγραφή.', 'error');
    }
  };

  const openCreate = () => {
    setCreateForm({ reminder_date: '', message: '', recurrence: 'none' });
    setShowCreate(true);
  };
  const closeCreate = useCallback(() => setShowCreate(false), []);

  // Escape key handler for modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (editingReminder) closeEdit();
        if (showCreate) closeCreate();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [editingReminder, showCreate, closeCreate]);
  const handleCreateChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  const createReminder = async () => {
    if (!pet || !clinic) return;
    if (!createForm.reminder_date || !createForm.message.trim()) {
      showToast('Συμπληρώστε ημερομηνία και μήνυμα.', 'error');
      return;
    }
    try {
      const { error } = await supabase
        .from('reminders')
                  .insert({
            clinic_id: clinic.id,
            owner_id: pet.owner_id,
            pet_id: pet.id,
            reminder_date: new Date(createForm.reminder_date).toISOString(),
            message: createForm.message.trim(),
            recipient_email: pet.owners?.email || null,
            recurrence: createForm.recurrence,
          });
      if (error) throw error;
      showToast('Η υπενθύμιση δημιουργήθηκε.', 'success');
      closeCreate();
      await fetchReminders();
    } catch (err) {
      console.error('Failed to create reminder:', err);
      showToast('Σφάλμα κατά τη δημιουργία.', 'error');
    }
  };

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (clinic && petId) {
      fetchPet();
      fetchReminders();
    }
  }, [clinic, petId, currentPage]);

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        navigate('/login');
        return;
      }
      setUser(user);
      const { clinic: clinicData } = await resolveClinicForUser(supabase, user.id);
      if (clinicData) setClinic(clinicData);
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchPet = async () => {
    try {
      const { data, error } = await supabase
        .from('pets')
        .select('id, name, species, breed, owner_id, owners:owner_id(first_name,last_name,email)')
        .eq('id', petId)
        .single();
      if (error) throw error;
      setPet(data);
    } catch (err) {
      console.error('Error fetching pet:', err);
      showToast('Σφάλμα φόρτωσης κατοικίδιου.', 'error');
    }
  };

  const fetchReminders = async () => {
    try {
      // Count
      let countQuery = supabase
        .from('reminders')
        .select('*', { count: 'exact', head: true })
        .eq('pet_id', petId)
        .eq('clinic_id', clinic.id);
      const { count, error: countError } = await countQuery;
      if (countError) throw countError;
      setTotalReminders(count || 0);

      // Data
      let dataQuery = supabase
        .from('reminders')
        .select('id, reminder_date, message, status, recipient_email, created_at, updated_at, recurrence')
        .eq('pet_id', petId)
        .eq('clinic_id', clinic.id)
        .order('reminder_date', { ascending: false });
      const from = (currentPage - 1) * perPage;
      const to = from + perPage - 1;
      const { data, error } = await dataQuery.range(from, to);
      if (error) throw error;
      setReminders(data || []);
    } catch (err) {
      console.error('Error fetching reminders:', err);
      showToast('Σφάλμα φόρτωσης υπενθυμίσεων.', 'error');
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };
  const goToNextPage = () => {
    if (currentPage < Math.ceil(totalReminders / perPage)) {
      goToPage(currentPage + 1);
    }
  };
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };
  const getPageNumbers = () => {
    const totalPages = Math.ceil(totalReminders / perPage);
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('el-GR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('el-GR');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!pet) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1A202C' }}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-white text-lg">Το κατοικίδιο δεν βρέθηκε</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />

      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast */}
        {toast.show && (
          <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                  toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
                }`
              }}
            >
              <div className="flex-shrink-0 mr-3">
                {toast.type === 'success' && (
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                )}
              </div>
              <div className="text-white font-medium flex-1">{toast.message}</div>
              <button onClick={() => setToast({ show: false, message: '', type: 'success' })} className="ml-4 text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <button
              onClick={() => navigate(`/pet-medical-history/${pet.id}`)}
              className="flex items-center px-4 py-2 rounded-lg font-medium text-white transition-colors w-fit"
              style={{ backgroundColor: '#4A5568' }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
              Πίσω στο Ιστορικό
            </button>
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap"
              style={{
                background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(6,182,212,0.15) 100%)',
                border: '1px solid rgba(59,130,246,0.3)'
              }}
            >
              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
              </svg>
              <span className="text-blue-400 font-medium text-sm">
                {totalReminders} {totalReminders === 1 ? 'υπενθύμιση' : 'υπενθυμίσεις'}
              </span>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden border p-4 lg:p-6" style={{ background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)', borderColor: '#374151', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Υπενθυμίσεις - {pet.name}</h1>
                <div className="text-gray-400 text-base lg:text-lg">{pet.species} • {pet.owners?.first_name} {pet.owners?.last_name}</div>
              </div>
              <div>
                <button onClick={openCreate} className="px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02]" style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}>
                  + Νέα Υπενθύμιση
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reminders List */}
        <div className="space-y-4 lg:space-y-6">
          {reminders.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <svg className="mx-auto h-16 w-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-3-3v6m9 3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              <div className="text-lg mb-2">Δεν βρέθηκαν υπενθυμίσεις</div>
              <div className="text-sm">Δοκιμάστε διαφορετικό όρο αναζήτησης</div>
            </div>
          ) : (
            reminders.map((rem) => (
              <div key={rem.id} className="rounded-xl overflow-hidden border transition-all duration-300 hover:border-emerald-500/30" style={{ backgroundColor: '#111827', borderColor: '#374151', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
                <div className="px-4 lg:px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.65), rgba(31,41,55,0.65))' }}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div>
                      <div className="text-sm text-gray-400">Υπενθύμιση για</div>
                      <h3 className="text-lg font-semibold text-white">{formatDateOnly(rem.reminder_date)}</h3>
                      {rem.recurrence && rem.recurrence !== 'none' && (
                        <div className="mt-1 text-xs text-gray-400">Επανάληψη: {rem.recurrence === 'monthly' ? 'Μηνιαία' : 'Ετήσια'}</div>
                      )}
                    </div>
                    <div className="flex flex-col sm:items-end gap-1.5">
                      <div className="text-xs text-gray-400">Δημ.: {formatDateTime(rem.created_at)}</div>
                      <div className="flex gap-1.5">
                        <button onClick={() => openEdit(rem)} className="flex items-center gap-1 px-2 py-1 sm:px-3 rounded-md text-white text-xs" style={{ backgroundColor: '#3B82F6' }} title="Επεξεργασία">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                          <span className="hidden sm:inline">Επεξεργασία</span>
                        </button>
                        {rem.status !== 'cancelled' && (
                          <button onClick={() => setCancelConfirm(rem)} className="flex items-center gap-1 px-2 py-1 sm:px-3 rounded-md text-white text-xs" style={{ backgroundColor: '#DC2626' }} title="Ακύρωση">
                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path></svg>
                            <span className="hidden sm:inline">Ακύρωση</span>
                          </button>
                        )}
                        <button onClick={() => setDeleteConfirm(rem)} className="flex items-center gap-1 px-2 py-1 sm:px-3 rounded-md text-white text-xs" style={{ backgroundColor: '#7F1D1D' }} title="Διαγραφή">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          <span className="hidden sm:inline">Διαγραφή</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 lg:p-6">
                  {rem.message && <div className="text-sm text-gray-300 whitespace-pre-wrap">{rem.message}</div>}
                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs lg:text-sm text-gray-400">
                    {rem.status && <div><span className="text-gray-500">Κατάσταση:</span> {rem.status}</div>}
                    {rem.recipient_email && <div><span className="text-gray-500">Email:</span> {rem.recipient_email}</div>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalReminders > perPage && (
          <div className="mt-8 flex flex-col items-center space-y-4">
            <div className="text-sm text-gray-400">
              Εμφάνιση {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalReminders)} από {totalReminders} υπενθυμίσεις
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={goToPreviousPage} disabled={currentPage === 1} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === 1 ? 'text-gray-500 cursor-not-allowed' : 'text-white hover:bg-gray-700'}`} style={{ backgroundColor: currentPage === 1 ? '#4A5568' : '#2D3748' }}>← Προηγούμενη</button>
              <div className="flex items-center space-x-1">
                {getPageNumbers().map((page, idx) => (
                  <button key={idx} onClick={() => typeof page === 'number' ? goToPage(page) : null} disabled={typeof page !== 'number'} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${typeof page === 'number' ? (page === currentPage ? 'text-white' : 'text-gray-300 hover:text-white hover:bg-gray-700') : 'text-gray-500 cursor-default'}`} style={{ backgroundColor: typeof page === 'number' && page === currentPage ? '#10B981' : '#2D3748' }}>{page}</button>
                ))}
              </div>
              <button onClick={goToNextPage} disabled={currentPage >= Math.ceil(totalReminders / perPage)} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage >= Math.ceil(totalReminders / perPage) ? 'text-gray-500 cursor-not-allowed' : 'text-white hover:bg-gray-700'}`} style={{ backgroundColor: currentPage >= Math.ceil(totalReminders / perPage) ? '#4A5568' : '#2D3748' }}>Επόμενη →</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Reminder Modal */}
      {editingReminder && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={(e) => e.target === e.currentTarget && closeEdit()}
        >
          <div
            className="rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto border animate-slideUp"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.98) 0%, rgba(31,41,55,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              borderColor: 'rgba(59,130,246,0.3)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 40px rgba(59,130,246,0.1)'
            }}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(59,130,246,0.1), rgba(6,182,212,0.05))' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.2), rgba(6,182,212,0.2))' }}>
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-white">Επεξεργασία Υπενθύμισης</h3>
                </div>
                <button onClick={closeEdit} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={(e) => { e.preventDefault(); saveReminderEdits(); }} className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Ημερομηνία</label>
                    <DatePicker
                      selected={editForm.reminder_date ? new Date(editForm.reminder_date + 'T00:00:00') : null}
                      onChange={(date) => {
                        if (date) {
                          const yyyy = date.getFullYear();
                          const mm = String(date.getMonth() + 1).padStart(2, '0');
                          const dd = String(date.getDate()).padStart(2, '0');
                          handleEditChange({ target: { name: 'reminder_date', value: `${yyyy}-${mm}-${dd}` } });
                        } else {
                          handleEditChange({ target: { name: 'reminder_date', value: '' } });
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Επιλέξτε ημερομηνία"
                      className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500/50 transition-all"
                      calendarClassName="dark-datepicker"
                      wrapperClassName="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Κατάσταση</label>
                    <select name="status" value={editForm.status} onChange={handleEditChange} className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500/50 transition-all" style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}>
                      <option value="pending">Σε αναμονή</option>
                      <option value="cancelled">Ακυρωμένη</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Πρότυπα μηνυμάτων</label>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => applyTemplateToEdit('monthly_pills')} className="px-3 py-1.5 rounded-lg text-white text-xs transition-all duration-200 hover:scale-[1.02]" style={{ backgroundColor: '#10B981' }}>Μηνιαία χάπια</button>
                    <button type="button" onClick={() => applyTemplateToEdit('vaccination')} className="px-3 py-1.5 rounded-lg text-white text-xs transition-all duration-200 hover:scale-[1.02]" style={{ backgroundColor: '#3B82F6' }}>Εμβολιασμός</button>
                    <button type="button" onClick={() => applyTemplateToEdit('recheck')} className="px-3 py-1.5 rounded-lg text-white text-xs transition-all duration-200 hover:scale-[1.02]" style={{ backgroundColor: '#F59E0B' }}>Επανέλεγχος</button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Μήνυμα</label>
                  <textarea name="message" value={editForm.message} onChange={handleEditChange} rows={4} className="w-full px-4 py-2.5 rounded-lg border text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500/50 transition-all" style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Επανάληψη</label>
                    <select name="recurrence" value={editForm.recurrence} onChange={handleEditChange} className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500/50 transition-all" style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}>
                      <option value="none">Καμία</option>
                      <option value="monthly">Μηνιαία</option>
                      <option value="yearly">Ετήσια</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#374151' }}>
                <button type="button" onClick={closeEdit} className="px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600/50" style={{ backgroundColor: 'rgba(55,65,81,0.5)' }}>Άκυρο</button>
                <button type="submit" className="px-6 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:scale-[1.02]" style={{ background: 'linear-gradient(90deg, #3B82F6, #2563EB)' }}>Αποθήκευση</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Reminder Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={(e) => e.target === e.currentTarget && closeCreate()}
        >
          <div
            className="rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto border animate-slideUp"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.98) 0%, rgba(31,41,55,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              borderColor: 'rgba(16,185,129,0.3)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 40px rgba(16,185,129,0.1)'
            }}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(16,185,129,0.1), rgba(6,182,212,0.05))' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(6,182,212,0.2))' }}>
                    <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-white">Νέα Υπενθύμιση</h3>
                </div>
                <button onClick={closeCreate} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={(e) => { e.preventDefault(); createReminder(); }} className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Ημερομηνία *</label>
                    <DatePicker
                      selected={createForm.reminder_date ? new Date(createForm.reminder_date + 'T00:00:00') : null}
                      onChange={(date) => {
                        if (date) {
                          const yyyy = date.getFullYear();
                          const mm = String(date.getMonth() + 1).padStart(2, '0');
                          const dd = String(date.getDate()).padStart(2, '0');
                          handleCreateChange({ target: { name: 'reminder_date', value: `${yyyy}-${mm}-${dd}` } });
                        } else {
                          handleCreateChange({ target: { name: 'reminder_date', value: '' } });
                        }
                      }}
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="Επιλέξτε ημερομηνία"
                      className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all"
                      calendarClassName="dark-datepicker"
                      wrapperClassName="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Επανάληψη</label>
                    <select name="recurrence" value={createForm.recurrence} onChange={handleCreateChange} className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all" style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}>
                      <option value="none">Καμία</option>
                      <option value="monthly">Μηνιαία</option>
                      <option value="yearly">Ετήσια</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Πρότυπα μηνυμάτων</label>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => applyTemplateToCreate('monthly_pills')} className="px-3 py-1.5 rounded-lg text-white text-xs transition-all duration-200 hover:scale-[1.02]" style={{ backgroundColor: '#10B981' }}>Μηνιαία χάπια</button>
                    <button type="button" onClick={() => applyTemplateToCreate('vaccination')} className="px-3 py-1.5 rounded-lg text-white text-xs transition-all duration-200 hover:scale-[1.02]" style={{ backgroundColor: '#3B82F6' }}>Εμβολιασμός</button>
                    <button type="button" onClick={() => applyTemplateToCreate('recheck')} className="px-3 py-1.5 rounded-lg text-white text-xs transition-all duration-200 hover:scale-[1.02]" style={{ backgroundColor: '#F59E0B' }}>Επανέλεγχος</button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Μήνυμα *</label>
                  <textarea name="message" value={createForm.message} onChange={handleCreateChange} required rows={4} placeholder="Γράψτε το μήνυμα υπενθύμισης..." className="w-full px-4 py-2.5 rounded-lg border text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all" style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }} />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#374151' }}>
                <button type="button" onClick={closeCreate} className="px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600/50" style={{ backgroundColor: 'rgba(55,65,81,0.5)' }}>Άκυρο</button>
                <button type="submit" className="px-6 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:scale-[1.02]" style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}>Δημιουργία</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setDeleteConfirm(null)}
        >
          <div
            className="rounded-2xl w-full max-w-sm border"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.98) 0%, rgba(31,41,55,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              borderColor: 'rgba(239,68,68,0.3)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 40px rgba(239,68,68,0.1)'
            }}
          >
            <div className="px-6 py-4 border-b" style={{ borderColor: '#374151' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(239,68,68,0.15)' }}>
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white">Διαγραφή Υπενθύμισης</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-sm mb-1">Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την υπενθύμιση;</p>
              <p className="text-gray-500 text-xs">{formatDateOnly(deleteConfirm.reminder_date)}</p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600/50 text-sm"
                  style={{ backgroundColor: 'rgba(55,65,81,0.5)' }}
                >
                  Άκυρο
                </button>
                <button
                  onClick={deleteReminder}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:scale-[1.02] text-sm"
                  style={{ background: 'linear-gradient(90deg, #DC2626, #B91C1C)' }}
                >
                  Διαγραφή
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelConfirm && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => e.target === e.currentTarget && setCancelConfirm(null)}
        >
          <div
            className="rounded-2xl w-full max-w-sm border"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.98) 0%, rgba(31,41,55,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              borderColor: 'rgba(220,38,38,0.3)',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 40px rgba(220,38,38,0.1)'
            }}
          >
            <div className="px-6 py-4 border-b" style={{ borderColor: '#374151' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(220,38,38,0.15)' }}>
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white">Ακύρωση Υπενθύμισης</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-sm mb-1">Είστε σίγουροι ότι θέλετε να ακυρώσετε αυτή την υπενθύμιση;</p>
              <p className="text-gray-500 text-xs">{formatDateOnly(cancelConfirm.reminder_date)}</p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setCancelConfirm(null)}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600/50 text-sm"
                  style={{ backgroundColor: 'rgba(55,65,81,0.5)' }}
                >
                  Πίσω
                </button>
                <button
                  onClick={cancelReminder}
                  className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:scale-[1.02] text-sm"
                  style={{ background: 'linear-gradient(90deg, #DC2626, #B91C1C)' }}
                >
                  Ακύρωση
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsSidebarOpen(false)} />
      )}
    </div>
  );
};

export default PetRemindersPage; 